import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { DndTable } from '@client/components/_ui/data-display/DndTable';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { complianceAuditTemplateFormStore } from '@client/components/compliance-audit-templates/store';
import { TemplateTabSearch } from '@client/components/compliance-audit-templates/TemplateTabSearch';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useAntdTable } from '@client/hooks/table';
import { trpc } from '@client/trpc/client';
import { createFileRoute, Outlet, useSearch } from '@tanstack/react-router';
import { Empty } from 'antd';
import { useMemo, useState } from 'react';

export const Route = createFileRoute(
    '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId/principles',
)({
    staticData: {
        ui: { useParentTitle: false },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <Principles />
        </PageController>
    );
}

/**
 * CUR-424 — working version's principles as a table + overlay add/edit pages
 * (evidence-types donor), diverging only where principles genuinely differ:
 * drag reorder (position is user-visible — SubStepTable's DndTable precedent)
 * and full-text Title/Description columns (their content is always visible,
 * never truncated). Edits on a PUBLISHED working version are allowed — the
 * server auto-clones a new DRAFT (design D4).
 */
function Principles() {
    const { complianceAuditTemplateId } = Route.useParams();
    // `version` = read-only snapshot viewing, validated by the details route.
    const { version } = useSearch({
        from: '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId',
    });
    const isReadOnly = complianceAuditTemplateFormStore.use.isReadonly();
    const { trpcUtils } = useTrpc();
    const navigate = useNavigate();

    const { data, isPending, error, refetch } =
        trpc.admin.complianceAuditTemplate.listComplianceAuditTemplatePrinciples.useQuery({
            complianceAuditTemplateId,
            version,
        });

    const deleteMutation = trpc.admin.complianceAuditTemplate.deleteComplianceAuditTemplatePrinciple.useMutation();
    const reorderMutation = trpc.admin.complianceAuditTemplate.reorderComplianceAuditTemplatePrinciples.useMutation();

    const [search, setSearch] = useState('');
    // Reorder needs the complete row set — drag is disabled while a search filters rows.
    const isSearchActive = search.trim() !== '';

    // Memoised — `useAntdTable` syncs internal state in an effect keyed on the
    // `rows` reference; a fresh array every render would loop that effect.
    const rows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return (data?.rows ?? [])
            .filter(
                (row) =>
                    !query ||
                    row.title.toLowerCase().includes(query) ||
                    (row.description ?? '').toLowerCase().includes(query),
            )
            .map((row) => ({ ...row, id: String(row.id) }));
    }, [data?.rows, search]);

    const { tableProps } = useAntdTable({
        rowKey: 'id',
        data: { rows, total: rows.length },
        columns: [
            {
                title: '#',
                dataIndex: 'order',
                width: 60,
                render: (value) => <span className="text-subtle-foreground">{Number(value) + 1}</span>,
            },
            {
                title: 'Title',
                dataIndex: 'title',
                width: 280,
                // Full text always visible — no ellipsis, wrap long words.
                render: (value) => <div className="whitespace-pre-wrap break-words font-medium">{value}</div>,
            },
            {
                title: 'Description',
                dataIndex: 'description',
                render: (value) => <div className="whitespace-pre-wrap break-words">{value || '-'}</div>,
            },
            ...(isReadOnly
                ? []
                : [
                      {
                          width: 60,
                          align: 'right' as const,
                          render: (_: unknown, row: (typeof rows)[number]) => (
                              <div onClick={(e) => e.stopPropagation()}>
                                  <Button.Delete
                                      tooltip="Delete principle"
                                      onConfirm={(onSuccess) =>
                                          deleteMutation.mutate(
                                              {
                                                  complianceAuditTemplateId,
                                                  selectedVersion: version,
                                                  complianceAuditTemplatePrincipleId: BigInt(row.id),
                                              },
                                              {
                                                  onSuccess(result) {
                                                      onSuccess();
                                                      if (result.createdNewDraft) {
                                                          message.info(
                                                              'A new draft version was created from the published version',
                                                          );
                                                      }
                                                      void trpcUtils.admin.complianceAuditTemplate.invalidate();
                                                  },
                                                  onError(error) {
                                                      message.destroy();
                                                      errorMessage.show(error);
                                                  },
                                              },
                                          )
                                      }
                                  />
                              </div>
                          ),
                      },
                  ]),
        ],
    });

    if (error) {
        return <InlineQueryError error={error} onRetry={() => void refetch()} title="Couldn't load principles" />;
    }

    return (
        <>
            <Outlet />
            <TableToolbar
                filters={
                    <TemplateTabSearch
                        storageKey="compliance-audit-template-principles-search"
                        placeholder="Search by title or description..."
                        onChange={setSearch}
                    />
                }
                actions={
                    !isReadOnly && (
                        <Button.Add.Link
                            to="/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId/principles/add"
                            params={{ complianceAuditTemplateId }}
                            variant="primary"
                            label="New principle"
                        />
                    )
                }
            />
            <div className={isReadOnly || isSearchActive ? '[&_td:first-child_button]:hidden' : undefined}>
                <DndTable
                    size="small"
                    onDragEnd={(keys) => {
                        // Guard against a drag racing the search state: a filtered
                        // set would persist a corrupted (incomplete) order.
                        if (isSearchActive) {
                            return;
                        }
                        reorderMutation.mutate(
                            {
                                complianceAuditTemplateId,
                                selectedVersion: version,
                                orderedIds: keys.map((key) => BigInt(key)),
                            },
                            {
                                onSuccess(result) {
                                    if (result.createdNewDraft) {
                                        message.info('A new draft version was created from the published version');
                                    }
                                    void trpcUtils.admin.complianceAuditTemplate.invalidate();
                                },
                                onError(error) {
                                    errorMessage.show(error);
                                    void trpcUtils.admin.complianceAuditTemplate.invalidate();
                                },
                            },
                        );
                    }}
                    locale={{ emptyText: <Empty description="No principles yet" /> }}
                    onRow={({ id }) => ({
                        className: 'cursor-pointer',
                        onClick() {
                            navigate({
                                to: '/admin-console/platform-content/compliance-audit-templates/details/$complianceAuditTemplateId/principles/edit/$complianceAuditTemplatePrincipleId',
                                params: {
                                    complianceAuditTemplateId,
                                    complianceAuditTemplatePrincipleId: BigInt(id),
                                },
                            });
                        },
                    })}
                    {...tableProps}
                    loading={isPending}
                />
            </div>
        </>
    );
}
